// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { TemplateModel } from '../../models/Template';
import { createId } from '../../index';

export interface TemplateFilters {
  orgId: string;
  workspaceId?: string;
  templateType?: TemplateModel['templateType'];
  isDefault?: boolean;
  category?: 'spec' | 'steering';
}

export interface CreateTemplateInput {
  templateType: TemplateModel['templateType'];
  name: string;
  content: string;
  version: string;
  isDefault: boolean;
  orgId: string;
  workspaceId: string;
  userId?: string;
  metadata?: TemplateModel['metadata'];
}

export interface UpdateTemplateInput {
  id: string;
  updates: Partial<TemplateModel>;
}

export class TemplateOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: TemplateFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<TemplateModel> {
    const collection = this.db.collections.templates;

    const selector: any = {
      orgId: { $eq: filters.orgId },
    };

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.templateType) {
      selector.templateType = { $eq: filters.templateType };
    }

    if (filters.isDefault !== undefined) {
      selector.isDefault = { $eq: filters.isDefault };
    }

    if (filters.category) {
      selector['metadata.category'] = { $eq: filters.category };
    }

    const queryOptions: QueryOptions<TemplateModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    return new QueryOperator<TemplateModel>(collection, queryOptions);
  }

  async findOne(templateId: string): Promise<TemplateModel | null> {
    const doc = await this.db.collections.templates.findOne(templateId).exec();
    return doc ? (doc.toJSON() as TemplateModel) : null;
  }

  async count(filters: TemplateFilters): Promise<number> {
    const collection = this.db.collections.templates;
    const selector: any = {
      orgId: { $eq: filters.orgId },
    };

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.templateType) {
      selector.templateType = { $eq: filters.templateType };
    }

    if (filters.isDefault !== undefined) {
      selector.isDefault = { $eq: filters.isDefault };
    }

    if (filters.category) {
      selector['metadata.category'] = { $eq: filters.category };
    }

    return await collection.count({ selector }).exec();
  }

  createMutation(): MutationOperator<TemplateModel, CreateTemplateInput> {
    return new MutationOperator<TemplateModel, CreateTemplateInput>(this.db.collections.templates, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString();
        const template: TemplateModel = {
          id: createId('template'),
          templateType: input.templateType,
          name: input.name,
          content: input.content,
          version: input.version,
          isDefault: input.isDefault,
          orgId: input.orgId,
          workspaceId: input.workspaceId,
          userId: input.userId,
          metadata: input.metadata,
          createdAt: now,
          updatedAt: now,
        };

        await collection.insert(template);
        return template;
      },
    });
  }

  updateMutation(): MutationOperator<TemplateModel, UpdateTemplateInput> {
    return new MutationOperator<TemplateModel, UpdateTemplateInput>(this.db.collections.templates, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec();
        if (!doc) throw new Error(`Template ${id} not found`);

        await doc.update({
          $set: { ...updates, updatedAt: new Date().toISOString() },
        });

        return doc.toJSON() as TemplateModel;
      },
    });
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.templates, {
      mutationFn: async (collection, templateId) => {
        const doc = await collection.findOne(templateId).exec();
        if (!doc) throw new Error(`Template ${templateId} not found`);

        await doc.remove();
      },
    });
  }
}
